import { Add, Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Link, TablePagination, Typography } from '@mui/material'
import { GreenText } from 'components'
import { Chip } from 'components/chips'
import { useAuth } from 'context'
import * as controllers from 'controllers'
import { PersonAddressDto, PersonDto } from 'dtos'
import { DEBOUNCE_DELAY } from 'localConstants'
import { enqueueSnackbar } from 'notistack'
import { PersonAddressesParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useDebounce } from 'utils'
import AddressesAddEditDialog from './AddressesAddEditDialog'

interface AddressesListCardProps {
  handleRefresh: () => void
  person: PersonDto
}

export default function AddressesListCard({ handleRefresh, person }: AddressesListCardProps) {
  const { permissions } = useAuth()

  const [address, setAddress] = useState<PersonAddressDto>(new PersonAddressDto())
  const [addressAddEditDialogOpen, setAddressAddEditDialogOpen] = useState<boolean>(false)
  const [addresses, setAddresses] = useState<PersonAddressDto[]>([])
  const [isGettingPersonAddresses, setIsGettingPersonAddresses] = useState<boolean>(false)
  const [isSavingAddress, setIsSavingAddress] = useState<boolean>(false)
  const [parameters, setParameters] = useState<PersonAddressesParameters>({ page: 0, pageSize: 3 })
  const [totalCount, setTotalCount] = useState<number>(0)

  const getPersonAddresses = useDebounce((personId: string, parameters: PersonAddressesParameters) => {
    setIsGettingPersonAddresses(true)
    controllers
      .getPersonAddresses(personId, parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setAddresses(response.value)
      })
      .finally(() => {
        setIsGettingPersonAddresses(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (person.personGuid) {
      getPersonAddresses(person.personGuid, parameters)
    }
  }, [person, parameters, getPersonAddresses])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <AddressesAddEditDialog
          initialValues={address}
          isDeclarantPerson={person.isDeclarant ?? false}
          loading={isSavingAddress}
          onClose={() => {
            setAddressAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsSavingAddress(true)

            const requestMethod = values.addressGuid ? controllers.updatePersonAddress : controllers.createPersonAddress

            requestMethod(person.personGuid!, values)
              .then(() => {
                enqueueSnackbar('Address Saved Successfully!', { variant: 'success' })
                // getPersonAddresses(person.personGuid!, parameters)
                setAddressAddEditDialogOpen(false)
                handleRefresh()
              })
              .finally(() => {
                setIsSavingAddress(false)
              })
          }}
          open={addressAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Addresses
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    endIcon={<Add />}
                    onClick={() => {
                      setAddress(new PersonAddressDto())
                      setAddressAddEditDialogOpen(true)
                    }}
                    variant='text'
                  >
                    ADD
                  </Button>
                )}
              </Grid>
            </Grid>

            {isGettingPersonAddresses && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingPersonAddresses &&
              addresses.map(address => (
                <Grid item xs={12} key={address.addressGuid}>
                  <Card variant='outlined'>
                    <CardContent>
                      <Grid container spacing={1}>
                        <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
                          <Grid item xs={12} sm={true} container alignItems='center' spacing={0}>
                            <Grid item>
                              <Typography sx={{ mr: 1 }} variant='h6'>
                                {address.addressNickname}
                              </Typography>
                            </Grid>

                            {address.isActive && (
                              <Grid item>
                                <Chip variant='green'>Active</Chip>
                              </Grid>
                            )}

                            {!address.isActive && (
                              <Grid item>
                                <Chip variant='gray'>Inactive</Chip>
                              </Grid>
                            )}

                            {address.isHomeAddress && (
                              <Grid item>
                                <Chip variant='green'>Home Address</Chip>
                              </Grid>
                            )}

                            {address.isWorkAddress && (
                              <Grid item>
                                <Chip variant='green'>Work Address</Chip>
                              </Grid>
                            )}

                            {address.isDeclarantAddress && (
                              <Grid item>
                                <Chip variant='green'>Declarant Address</Chip>
                              </Grid>
                            )}

                            {address.isTitleClerkAddress && (
                              <Grid item>
                                <Chip variant='green'>Title Clerk Address</Chip>
                              </Grid>
                            )}
                          </Grid>

                          <Grid item xs={12} sm='auto' alignItems='flex-end'>
                            {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                              <Button
                                endIcon={<Edit />}
                                onClick={() => {
                                  setAddress(address)
                                  setAddressAddEditDialogOpen(true)
                                }}
                                variant='text'
                              >
                                EDIT
                              </Button>
                            )}
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <Divider />
                        </Grid>

                        {address.phone && (
                          <Grid item xs={12}>
                            <Typography variant='body1'>
                              <Link href={'tel:' + address.phone}>{address.phone}</Link>
                            </Typography>
                          </Grid>
                        )}

                        {address.email && (
                          <Grid item xs={12}>
                            <Typography variant='body1'>
                              <Link href={'mailto:' + address.email}>{address.email}</Link>
                            </Typography>
                          </Grid>
                        )}

                        <Grid item xs={12}>
                          <Typography variant='body1'>{address.address1}</Typography>
                          {address.address2 && <Typography variant='body1'>{address.address2}</Typography>}
                          <Typography variant='body1'>{address.city}</Typography>
                          {address.county && <Typography variant='body1'>{address.county}</Typography>}
                          <Typography variant='body1'>
                            {address.stateProvince} {address.postalCode}
                          </Typography>
                          <Typography variant='body1'>{address.country}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}

            <Grid item xs={12}>
              <TablePagination
                component='div'
                count={totalCount}
                onPageChange={(_, page: number) => {
                  setParameters({
                    ...parameters,
                    page
                  })
                }}
                onRowsPerPageChange={e => {
                  setParameters({
                    ...parameters,
                    page: 0,
                    pageSize: e.target.value as unknown as number
                  })
                }}
                page={parameters.page}
                rowsPerPage={parameters.pageSize}
                labelRowsPerPage='Cards per page:'
                rowsPerPageOptions={[
                  { label: '1', value: 1 },
                  { label: '3', value: 3 },
                  { label: '5', value: 5 },
                  { label: '10', value: 10 },
                  { label: '25', value: 25 },
                  { label: '50', value: 50 },
                  { label: '100', value: 100 }
                ]}
                SelectProps={{ fullWidth: false }}
              />
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Add Button:**

Clicking the "Add" button will open the Add Address Dialog for creating a new 
address entry.

**Address Sub Card:**

Each entered address is displayed as a separate Address Sub Card.

**Address Nickname:**

The title of each Address Sub Card is displayed as the Address Nickname.

**Chips:**

Each Address Sub Card can display one or more chips, indicating the address 
type(s). Possible chips include:

- Active
- Inactive
- Home Address
- Work Address
- Declarant Address
- Title Clerk Address 

**Edit Button:**

Clicking the Edit button on an Address Sub Card will open the Edit Addresses 
Dialog.

**Address Sub Card Address Information:**

Each Address Sub Card lists the entered address details associated with the 
respective address. If no details are entered, the field may be left blank or display 
a placeholder.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
